import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PHONE_COUNTRIES,
  DEFAULT_PHONE_COUNTRY,
  OTHER_PHONE_COUNTRY,
  parsePhone,
} from '../utils/phone';
import './PhoneInput.css';

// Telefoonveld met landkeuze (standaard België, +32). Gecontroleerd door de
// ouder: `country` + `value` (het nummer zoals ingetypt). Bij het verlaten van
// het veld wordt een geldig nummer netjes herschreven (bv. "0470123456" ->
// "470 12 34 56") en wisselt het land mee als er een andere landcode werd
// ingetypt (+31 ...). De ouder roept parsePhone() zelf aan bij het versturen.
function PhoneInput({ country, value, onCountryChange, onValueChange, invalid, required }) {
  const { t, i18n } = useTranslation();

  const options = useMemo(() => {
    let names = null;
    try {
      names = new Intl.DisplayNames([i18n.language || 'nl'], { type: 'region' });
    } catch (e) {
      names = null;
    }
    const label = (c) => `${(names && names.of(c.code)) || c.code} (+${c.dial})`;
    const be = PHONE_COUNTRIES.find((c) => c.code === DEFAULT_PHONE_COUNTRY);
    const rest = PHONE_COUNTRIES.filter((c) => c.code !== DEFAULT_PHONE_COUNTRY)
      .map((c) => ({ code: c.code, label: label(c) }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language || 'nl'));
    return [{ code: be.code, label: label(be) }, ...rest];
  }, [i18n.language]);

  const parsed = value ? parsePhone(value, country) : null;
  const isOther = country === OTHER_PHONE_COUNTRY;

  const handleBlur = () => {
    if (!value) return;
    const result = parsePhone(value, country);
    if (!result.valid) return;
    if (result.country === OTHER_PHONE_COUNTRY) {
      onCountryChange(OTHER_PHONE_COUNTRY);
      onValueChange(result.formatted);
    } else {
      onCountryChange(result.country);
      onValueChange(result.national);
    }
  };

  return (
    <>
      <div className="phone-input-row">
        <select
          className={`phone-input-country${invalid ? ' field-invalid' : ''}`}
          value={country}
          onChange={(e) => onCountryChange(e.target.value)}
          aria-label={t('activate.phoneCountry')}
        >
          {options.map((o) => (
            <option key={o.code} value={o.code}>{o.label}</option>
          ))}
          <option value={OTHER_PHONE_COUNTRY}>{t('activate.phoneOtherCountry')}</option>
        </select>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          className={`phone-input-number${invalid ? ' field-invalid' : ''}`}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          onBlur={handleBlur}
          required={required}
          aria-label={t('activate.phone')}
        />
      </div>
      {isOther && !value && (
        <small className="phone-input-hint">{t('activate.phoneOtherHint')}</small>
      )}
      {parsed && parsed.valid && (
        <small className="phone-input-hint phone-input-ok">
          {t('activate.phoneSavedAs', { number: parsed.formatted })}
        </small>
      )}
    </>
  );
}

export default PhoneInput;
